/**
 * 4×4 宫格布局（速查表 28 节：外圈 12 宫 巳午未申/辰酉/卯戌/寅丑子亥，中心 2×2 中宫留空）
 * 纯函数，无副作用，可单测
 */
import type { Zhi } from '../types/base';

/** 4×4 网格坐标（0-3 行/列） */
export interface GridPos {
  row: number;
  col: number;
}

/** 地支 → 网格坐标（固定映射，唯一事实源） */
export const GRID_POS: Record<Zhi, GridPos> = {
  巳: { row: 0, col: 0 },
  午: { row: 0, col: 1 },
  未: { row: 0, col: 2 },
  申: { row: 0, col: 3 },
  辰: { row: 1, col: 0 },
  酉: { row: 1, col: 3 },
  卯: { row: 2, col: 0 },
  戌: { row: 2, col: 3 },
  寅: { row: 3, col: 0 },
  丑: { row: 3, col: 1 },
  子: { row: 3, col: 2 },
  亥: { row: 3, col: 3 },
};

/** 外圈 12 宫按网格行序遍历顺序（渲染骨架用） */
export const GRID_ZHIS: Zhi[] = [
  '巳', '午', '未', '申',
  '辰', '酉',
  '卯', '戌',
  '寅', '丑', '子', '亥',
];

/** 该格是否为中宫（row/col ∈ [1,2]） */
export function isCenter(row: number, col: number): boolean {
  return row >= 1 && row <= 2 && col >= 1 && col <= 2;
}

/** 地支 → 网格坐标 */
export function gridPos(zhi: Zhi): GridPos {
  return GRID_POS[zhi];
}

/** 地支序表（子=0 … 亥=11） */
export const ZHI_ORDER: Zhi[] = ['子', '丑', '寅', '卯', '辰', '巳', '午', '未', '申', '酉', '戌', '亥'];

/** 地支序号（子=0 … 亥=11），对宫 = (idx+6)%12 */
export function zhiIndex(zhi: Zhi): number {
  return ZHI_ORDER.indexOf(zhi);
}

/** 对宫地支（(idx+6)%12） */
export function oppositeZhi(zhi: Zhi): Zhi {
  return ZHI_ORDER[(zhiIndex(zhi) + 6) % 12];
}

/** 命宫三方四正四宫地支（速查表 28 节十二宫序：财帛=命宫顺数第5宫 +4；迁移=对宫 +6；官禄=顺数第9宫 +8） */
export function sanfangSizheng(ming: Zhi): { ming: Zhi; cai: Zhi; qian: Zhi; guan: Zhi } {
  const i = zhiIndex(ming);
  return {
    ming,
    cai: ZHI_ORDER[(i + 4) % 12],
    qian: ZHI_ORDER[(i + 6) % 12],
    guan: ZHI_ORDER[(i + 8) % 12],
  };
}

/**
 * 宫格内边线中心点（viewBox 400×400，每格=100；中宫 2×2 = [100,300]²）
 * 面向盘心的内边中点：顶行=下边中点、底行=上边中点、左列=右边中点、右列=左边中点；
 * 角落宫（巳申寅亥）与中宫仅共享一个角点，取该角点
 * （三方四正虚线端点：两点都在中宫矩形边界上，线段因凸性全部位于中宫内）
 */
export function innerEdgeCenter(row: number, col: number): { x: number; y: number } {
  if (row === 0 && col === 0) return { x: 100, y: 100 };
  if (row === 0 && col === 3) return { x: 300, y: 100 };
  if (row === 3 && col === 0) return { x: 100, y: 300 };
  if (row === 3 && col === 3) return { x: 300, y: 300 };
  if (row === 0) return { x: col * 100 + 50, y: 100 };
  if (row === 3) return { x: col * 100 + 50, y: 300 };
  if (col === 0) return { x: 100, y: row * 100 + 50 };
  return { x: 300, y: row * 100 + 50 };
}

/** 裁剪后线段（保留矩形内部分） */
export interface ClippedSeg {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

/**
 * Liang-Barsky：线段与轴对齐矩形求交，返回矩形内的线段；无交返回 null
 * （三方四正虚线裁剪用：只显示中宫 2×2 范围内的线段，不延伸到外圈宫格）
 */
export function clipSegment(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  xmin: number,
  ymin: number,
  xmax: number,
  ymax: number,
): ClippedSeg | null {
  let t0 = 0;
  let t1 = 1;
  const dx = x2 - x1;
  const dy = y2 - y1;
  const p = [-dx, dx, -dy, dy];
  const q = [x1 - xmin, xmax - x1, y1 - ymin, ymax - y1];
  for (let i = 0; i < 4; i++) {
    if (p[i] === 0) {
      // 与裁剪边平行：整段在界外则无交
      if (q[i] < 0) return null;
    } else {
      const r = q[i] / p[i];
      if (p[i] < 0) {
        // 入边
        if (r > t1) return null;
        if (r > t0) t0 = r;
      } else {
        // 出边
        if (r < t0) return null;
        if (r < t1) t1 = r;
      }
    }
  }
  return { x1: x1 + t0 * dx, y1: y1 + t0 * dy, x2: x1 + t1 * dx, y2: y1 + t1 * dy };
}
